import { Box, Stack, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Loader, Modal, ModalSaveSection, NeighborhoodLotsDropdown } from '../../../../components';
import { useSnackbar } from 'notistack';
import { buySpec, getNeighborhoodJob } from '../../../../fetch';
import { formatMoney } from '../../../../helpers';
import { INeighborhoodJob } from '../../../../models';
import { SalesNeighborhoodDropdown } from '../../../../components/dropdowns/sales-neighborhood-dropdown';

interface IBuySpecModal {
  personId: number;
  open: boolean;
  onClose: () => void;
}

export const BuySpecModal: FC<IBuySpecModal> = ({ personId, onClose, open }) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | null>('');
  const [selectedLotId, setSelectedLotId] = useState<string | null>('');
  const [plan, setPlan] = useState<INeighborhoodJob | null>(null);
  const [isBuying, setIsBuying] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);

  const fetchPlanPrice = async () => {
    try {
      setIsLoadingPlan(true);
      const res = await getNeighborhoodJob({
        neighborhoodId: selectedNeighborhoodId,
        lotNumber: selectedLotId
      });
      setPlan(res);
    } catch (error) {
    } finally {
      setIsLoadingPlan(false);
    }
  };

  useEffect(() => {
    if (selectedLotId && selectedNeighborhoodId) {
      fetchPlanPrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhoodId, selectedLotId]);

  const handleClose = () => {
    setSelectedNeighborhoodId('');
    setSelectedLotId('');
    setPlan(null);
    onClose();
  };

  return (
    <Modal open={open} title="Buy Spec" maxWidth="xs" onClose={handleClose}>
      {(isBuying || isLoadingPlan) && <Loader type="overlay" position="centered" />}
      <Box mt={1}>
        <Stack gap={1}>
          <SalesNeighborhoodDropdown
            isRequired
            value={selectedNeighborhoodId}
            handleChange={value => {
              setSelectedNeighborhoodId(value as string);
              setSelectedLotId('');
            }}
            apiParams={{ hasSpecs: true, forAgentNewBuilds: true, isActive: true }}
          />
          <NeighborhoodLotsDropdown
            isRequired
            value={selectedLotId}
            isDisabled={!selectedNeighborhoodId}
            handleChange={value => setSelectedLotId(value as string)}
            apiParams={{ neighborhoodId: selectedNeighborhoodId, isSpec: true }}
          />
          {plan && !!plan.totalPrice && (
            <Stack>
              <Box>
                <Typography component="span" color="primary">
                  Plan:
                </Typography>{' '}
                <Typography color="secondary" component="span">
                  {plan.planName}
                </Typography>
              </Box>
              <Box>
                <Typography component="span" color="primary">
                  Price:
                </Typography>{' '}
                <Typography color="secondary" component="span">
                  {formatMoney(plan.totalPrice)}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>
      <ModalSaveSection
        saveButtonText="Continue"
        isSaveDisabled={!selectedNeighborhoodId || !selectedLotId}
        handleCancel={handleClose}
        handleSave={async () => {
          try {
            setIsBuying(true);
            await buySpec(plan.buildId, {
              clientId: personId
            });
            history.push(
              `/sales/contracting/build-options/${plan.buildId}?redirect=/sales/contracting/purchasers/${plan.buildId}/builds `
            );
            enqueueSnackbar('Spec bought!', {
              variant: 'success'
            });
          } catch (error: any) {
            enqueueSnackbar(error?.Detail ?? `Error loading buying spec, please try again.`, {
              variant: 'error'
            });
          } finally {
            setIsBuying(false);
          }
        }}
      />
    </Modal>
  );
};
